import { horizontalBarSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/settingsByViz/horizontalBar';
import { BarChartOption } from './BarChartOption';
import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { getValidSettings } from './helpers';
import { EChartsOption } from 'echarts-for-react';
import { EChartsSerie, SerieLabel } from './types';
import { SeriePointT } from '../../types';
import { getDatasetValueKey } from 'components/ChartContainer/components/helpers/datasetHelpers';
import { getYAxisNameGap, getYMaxValueFormatter } from 'helpers/axis/yAxis';
import { wrap } from 'helpers/labels/wrapLabels';
import { formatDisplay } from 'helpers/formatHelper';
import {
    formatAxisLabel,
    formatTooltipTitle,
    getTooltipString,
} from 'helpers/chartHelper';
import { getAxisLabelWidthFactor } from 'helpers/axis/xAxis';
import { getDataZoom } from 'helpers/chart/dataZoom';
import { isDate, isDateOrYear } from 'helpers/helpers';

const NameHorizontalWrapFactor = 30;

export class HorizontalBarChartOption extends BarChartOption {
    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        super(
            orderedColumns,
            settings,
            data,
            width,
            height,
            dataZoomInterval,
            isPdfModeActivated,
            isPngModeActivated,
            isChartViewedOnDashboard,
        );

        // Delete unhandled settings:
        // IMPORTANT: Because we inherit from BarChartOption
        // which also filter settings, we need to do this after
        // calling the super constructor; otherwise we would apply
        // both (bar + horizontal bar) filter and not just the filter
        // for horizontal bar.
        const properSettings = getValidSettings(
            settings,
            horizontalBarSettings,
        );
        this.settings = properSettings;

        this.filterUnknownDateOnXAxis();
    }

    /**
     * IMPORTANT: We have to do the same pre-processing hack that PolarChartOption does.
     * See the comment in PolarChartOption for an extensive explanation, but to put it simply:
     * The backend can return value with an undefined date, and in some case, EChart will
     * display it as a literal "undefined" serie, which is undesirable.
     */
    private filterUnknownDateOnXAxis() {
        if (
            this.orderedColumns.x !== undefined &&
            isDate(this.orderedColumns.x.type)
        ) {
            // Filter data to remove unknown date from x axis.
            this.data = this.data.filter(
                (d) => d[this.orderedColumns.x!.key] !== 'unknown',
            );
        }
    }

    protected override handleSeriesGoalValue(
        series: EChartsSerie[],
    ): EChartsSerie[] {
        if (this.settings.goalValue === undefined) {
            return series;
        }
        const superSeries = super.handleSeriesGoalValue(series);
        return superSeries.map((s) => ({
            ...s,
            markLine: {
                ...s.markLine,
                data: [{ xAxis: this.settings.goalValue }],
            },
        }));
    }

    protected override getLabel(
        serie: EChartsSerie,
        serieIndex: number,
    ): SerieLabel {
        const label = {
            ...super.getLabel(serie, serieIndex),
            position: this.settings.areLabelsShownInside ? 'inside' : 'right',
            rotate: 0,
        };

        // @ts-expect-error [typing]
        return label;
    }

    // This is more or less a reimplementation of {@see CartesianChartOption.getYAxis}
    // but with all the needed tweak to make it work for horizontal bar chart.
    // It's also simplified since we don't need to handle things like secondary axis.
    protected override getXAxis(): EChartsOption['xAxis'] {
        return {
            name: this.settings.yAxisTitle || this.orderedColumns.y.label,
            nameGap: getYAxisNameGap(this.settings.areTicksRotated, true),
            nameLocation: 'middle',
            nameTextStyle: {
                color: '#666',
                fontFamily: 'Poppins',
            },
            max: getYMaxValueFormatter(
                false,
                this.orderedColumns.y.type,
                this.settings.goalValue,
            ),
            axisLabel: {
                fontSize: this.getAxisLabelFontSize(this.orderedColumns.y),
                fontFamily: 'Poppins',
                color: '#666',
                rotate: this.settings.areTicksRotated ? 30 : 0,
                formatter: (value: number) =>
                    formatDisplay(
                        value,
                        this.orderedColumns.y.type,
                        this.getDecimalPrecision(),
                        this.settings.displayBy,
                    ),
            },
            axisLine: { show: false },
            axisTick: { show: false },
        };
    }

    // Force the X (which is actually the Y) axis to be a category.
    protected getXAxisType(): 'category' | 'time' | 'value' {
        return 'category';
    }

    // This is more or less a reimplementation of {@see CartesianChartOption.getXAxis}
    // but with all the needed tweak to make it work for horizontal bar chart.
    protected override getYAxis(): EChartsOption['yAxis'] {
        if (this.orderedColumns.x === undefined) {
            return undefined;
        }
        // Y axis is the X axis in horizontal bar chart.
        const { type, scale, label } = this.orderedColumns.x;

        // Horizontal bar chart only support category, so we force it.
        const yAxisType = 'category';

        let yAxis: EChartsOption['xAxis'] = {
            type: yAxisType,
            scale: true,
            boundaryGap: ['5%', '5%'],
            name: wrap(
                this.settings.xAxisTitle || label,
                this.width,
                NameHorizontalWrapFactor,
                3,
            ),
            nameLocation: 'end',
            nameGap: 15,
            nameTextStyle: {
                color: '#666',
                fontFamily: 'Poppins',
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#666666',
                },
            },
            splitArea: { show: false },
            splitLine: { show: false },
        };

        const axisLabel = {
            fontFamily: 'Poppins',
            fontSize: this.getAxisLabelFontSize(this.orderedColumns.x),
            align: 'right',
            interval: this.getXAxisLabelInterval(),
        };

        // Handle interval and ticks.
        const { interval, ticks } = this.getIntervalAndTick();
        if (ticks && ticks.length < 15 && interval) {
            yAxis = {
                ...yAxis,
                interval,
            };
        }

        const formatter = (value: string | undefined): string => {
            const formatted = formatAxisLabel(
                value,
                type,
                this.getDecimalPrecision(),
                this.settings.displayBy,
                interval,
                scale,
                this.settings.areTicksRotated,
                this.treeValueTranslations,
            ).toString();

            return wrap(
                formatted,
                this.width,
                getAxisLabelWidthFactor(this.settings.verticalAxisWidth),
                this.settings.wrapVerticalAxisLabels ? 2 : 1,
            );
        };

        yAxis = {
            ...yAxis,
            axisLabel: {
                ...axisLabel,
                formatter,
            },
        };

        return yAxis;
    }

    // swap the encode x and y to make it horizontal.
    protected override getSeries(): EChartsSerie[] {
        const series = super.getSeries();
        return series.map((s) => ({
            ...s,
            // @ts-expect-error [typing] echart doesnt export encode in its type.
            encode: { x: s.encode.y, y: s.encode.x },
        }));
    }

    // Update the helper function to have it work for horizontal chart.
    protected override getDatasetValueKey(point: SeriePointT): string {
        return getDatasetValueKey(point, true);
    }

    // Add the y index. Necessary to show it on the Y axis instead
    // of the x axis.
    protected override getDatazoom(): EChartsOption['dataZoom'] {
        if (!this.settings.isDataZoomEnabled) {
            return undefined;
        }

        return {
            ...getDataZoom({
                isHorizontal: true,
                show: !(
                    this.isChartViewedOnDashboard ||
                    this.isPdfModeActivated ||
                    this.isPngModeActivated
                ),
                decimalPrecision: this.getDecimalPrecision(),
                displayBy: this.settings.displayBy,
                treeValueTranslations: this.treeValueTranslations,
                xType: this.orderedColumns.x!.type,
                xScale: this.orderedColumns.x!.scale,
                xInterval: undefined,
                color: this.getColor(),
                dataZoomInterval: this.dataZoomInterval,
            }),
            yIndex: 0,
        };
    }

    // We have to override it so that the stack value gets the x dimension and not
    // the y.
    protected override getFormattedSeriesValues(params: SeriePointT[]) {
        const value = params[0].axisValue;
        let title = formatTooltipTitle(
            value,
            this.orderedColumns.x!.type,
            this.orderedColumns.x?.scale,
            this.interval,
            this.treeValueTranslations,
        ).toString();

        // For stacked bars, we also display the sum of all series in the bar.
        if (params.length > 1 && this.settings.isStackedBars) {
            const dimension = params[0].dimensionNames[params[0].encode.x[0]];
            const sum = params
                .map((d) => Number(d.value[dimension]))
                .filter((d) => d) // filter NaN, 0 and undefined
                .reduce((acc, d) => acc + d, 0);
            title = `${title} : ${formatDisplay(
                sum,
                this.orderedColumns.y.type,
                this.getDecimalPrecision(),
                this.settings.displayBy,
            )}`;
        }

        // We only display the evolution % when the X axis is a time
        // and when we are not doing a pluri-annual comparision.
        const isYearlyComparision = this.settings.isYearlyComparison ?? false;
        const showEvolution =
            isDateOrYear(this.orderedColumns.x?.type) && !isYearlyComparision;
        const tooltipSeries = params.map((d) =>
            this.getFormattedSerieValue(d, showEvolution),
        );
        return getTooltipString(title.toString(), tooltipSeries);
    }
}
